import React from "react";
import { useToast } from "../../../components/common/Toast";
import { LotSection } from "./LotSection";
import { StopwatchSection } from "./StopWatch";
import { RoastProfileSection } from "./RoastProfile";
import { RoastResultSection } from "./RoastResultSection";
import { type Recipe } from "../../../types/recipe";
import { recipeService } from "../../../services/recipeService";
import { RoastFormHeaderSection } from "./RoastFormHeaderSection";
import { RoastFormNotesSection } from "./RoastFormNotesSection";
import { RoastFormActions } from "./RoastFormActions";
import { useRoastFormState } from "./useRoastFormState";
import { useLanguage } from "../../../features/language/useLanguage";

interface RoastFormProps {
  mode?: 'roast' | 'recipe';
  initialData?: Recipe;
  onSuccess?: () => void;
  onCancel?: () => void;
  readOnly?: boolean;
}

export const RoastForm: React.FC<RoastFormProps> = React.memo(({ mode = 'roast', initialData, onSuccess, onCancel, readOnly = false }) => {
  const { t } = useLanguage();
  const {
    unit,
    setUnit,
    date,
    setDate,
    time,
    setTime,
    roaster,
    setRoaster,
    machine,
    machineTypeId,
    handleMachineChange,
    recipeName,
    setRecipeName,
    notes,
    setNotes,
    roastType,
    setRoastType,
    beans,
    selectedBean,
    machineOptions,
    lotOptions,
    lots,
    totalInputWeight,
    roastData,
    handleBeanChange,
    handleLotChange,
    handleWeightChange,
    addLotRow,
    removeLotRow,
    handleStageDataChange,
    handleWeightInputChange,
    handleWeightOutputChange,
    stage,
    seconds,
    handleMainButton,
    handleStartRunning,
    handlePauseRunning,
    handleRestart,
    handleProfileChange,
    handleAgtronChange,
  } = useRoastFormState({ mode, initialData });

  const { showToast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Prepare payload for both modes
    const stageSummary = roastData.stageStates.reduce((acc, s) => ({
      ...acc,
      [s.key]: { time: s.time, temp: s.temp }
    }), {});

    // Calculate shrinkage
    const wIn = Number(roastData.weightInput || 0);
    const wOut = Number(roastData.weightOutput || 0);
    const shrinkage = (wIn > 0 && wOut > 0) ? ((wIn - wOut) / wIn) * 100 : 0;
    
    // Determine total time from end stage or profile
    const endStage = roastData.stageStates.find(s => s.key === 'end');
    let totalTime = 0;
    if (endStage && endStage.time) {
         const [mm, ss] = endStage.time.split(':').map(Number);
         totalTime = (mm * 60) + ss;
    }
    
    const payload = {
       name: recipeName || (mode === 'roast' ? `${t('roast.page.title')} - ${new Date().toLocaleString()}` : ''),
       itemId: selectedBean,
       machine,
       machineTypeId: machineTypeId || undefined,
       roastType,
       targetBeanWeight: wIn,
       targetWeightOutput: wOut,
       expectedShrinkage: shrinkage,
       agtron: {
         ...roastData.agtron,
         wholeBean: roastData.agtron.wholeBean === '' ? undefined : Number(roastData.agtron.wholeBean),
         groundBean: roastData.agtron.groundBean === '' ? undefined : Number(roastData.agtron.groundBean),
       },
       stageSummary,
       profile: roastData.profile,
       internalNotes: notes,
       totalTime: totalTime / 60, // in minutes
    };

    if (mode === 'recipe') {
      try {
        if (initialData?.id) {
           await recipeService.update(initialData.id, payload);
        } else {
           await recipeService.create(payload);
        }
        
        if (onSuccess) onSuccess();
      } catch (error) {
        console.error(t('roast.form.errors.saveRecipeFailed'), error);
        showToast(t('roast.form.errors.saveRecipeFailed'), "error");
      }
    } else {
      // Mode 'roast' - Save as Recipe (Roast Log)
      try {
        await recipeService.create(payload);
        showToast(t('roast.form.success.savedSuccessfully'), "success");
        if (onSuccess) onSuccess();
      } catch (error) {
        console.error(t('roast.form.errors.saveRoastFailed'), error);
        showToast(t('roast.form.errors.saveRoastFailed'), "error");
      }
    }
  };

  return (
    <form className="space-y-8" onSubmit={handleSubmit}>
      <RoastFormHeaderSection
        mode={mode}
        recipeName={recipeName}
        onRecipeNameChange={setRecipeName}
        machine={machine}
        onMachineChange={handleMachineChange}
        machineOptions={machineOptions}
        date={date}
        onDateChange={setDate}
        time={time}
        onTimeChange={setTime}
        roaster={roaster}
        onRoasterChange={setRoaster}
        beans={beans}
        selectedBean={selectedBean}
        onBeanChange={handleBeanChange}
        readOnly={readOnly}
      />

      {/* Section 2 */}
      {mode === 'roast' && (
        <LotSection
          lots={lots}
          lotOptions={lotOptions}
          onLotChange={handleLotChange}
          onWeightChange={handleWeightChange}
          onAdd={addLotRow}
          onRemove={removeLotRow}
          unit={unit}
          onToggle={setUnit}
          readOnly={readOnly}
        />
      )}

		{mode === 'roast' && lots.some((l) => l.lotId !== "") && (
        <StopwatchSection
          stage={stage}
          onMainClick={handleMainButton}
          onStart={handleStartRunning}
          onPause={handlePauseRunning}
          onRestart={handleRestart}
          readOnly={readOnly}
        />
		)}

      <RoastProfileSection 
        seconds={seconds} 
        stage={stage} 
        onDataChange={handleProfileChange}
        mode={mode}
        initialProfile={roastData.profile}
        useStopwatchMode={mode === 'roast'}
        stages={roastData.stageStates || []}
        readOnly={readOnly}
      />

      <RoastResultSection
        stages={roastData.stageStates}
        onStageChange={handleStageDataChange}
        weightInput={roastData.weightInput}
        calculatedWeight={totalInputWeight}
        onWeightInputChange={handleWeightInputChange}
        weightOutput={roastData.weightOutput}
        onWeightOutputChange={handleWeightOutputChange}
        agtronWhole={roastData.agtron.wholeBean}
        agtronGround={roastData.agtron.groundBean}
        onAgtronChange={handleAgtronChange}
        mode={mode}
        roastType={roastType}
        onRoastTypeChange={setRoastType}
        unit={unit}
        onToggle={setUnit}
        readOnly={readOnly}
      />
      <RoastFormNotesSection notes={notes} onNotesChange={setNotes} readOnly={readOnly} />
      <RoastFormActions 
        onCancel={onCancel} 
        canDownload={roastData.profile.length > 0}
        readOnly={readOnly}
        downloadData={{
          recipeName,
          roastName: recipeName || (mode === 'roast' ? `${t('roast.page.title')} - ${new Date().toLocaleString()}` : t('roast.form.unnamedProfile')),
          machineName: machineOptions.find(o => o.value === machine)?.text || machine,
          beanName: beans.find(b => b.id === selectedBean)?.name || t('roast.form.unknownBean'),
          date,
          time,
          roastData,
          roastType,
          notes: notes
        }}
      />
    </form>
  );
});

